import React, { useState, useMemo } from 'react';
import { OrganizationProgressItem } from '../../../api/adminOrganizationsService';
import OrgsTable   from './OrgsTable';
import CompanyInfo from './CompanyInfo';
import InProgressEmployeeSelector from './InProgressEmployeeSelector';
import { sendOrganizationEmail, finishAllContacts } from '../../../api/adminOrganizationsService';

interface Props {
  items:  OrganizationProgressItem[];
  reload: () => void;
}

const InProgressPane: React.FC<Props> = ({ items, reload }) => {
  const [selected, setSelected] = useState<OrganizationProgressItem | null>(null);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [sending, setSending] = useState<'lead' | 'reminder' | null>(null);
  const [finalizing, setFinalizing] = useState(false);

  const tableItems = (items ?? []).map((it) => {
    const pend = it?.employees_pending ?? [];
    const cont = it?.employees_contacted ?? [];
    return {
      org: it.org,
      report: it.report,
      employees: [...pend, ...cont],
      total_candidates:
        typeof it.total_candidates === 'number'
          ? it.total_candidates
          : pend.length + cont.length,
    };
  });

  // Buckets para habilitar/enviar correctamente
  const { selectedForLead, selectedForReminder, invalidSelection } = useMemo(() => {
    if (!selected) return { selectedForLead: [], selectedForReminder: [], invalidSelection: [] as string[] };

    const pendingSet = new Set((selected.employees_pending ?? []).map(e => e.email));
    const noRemSet   = new Set((selected.employees_contacted ?? [])
                               .filter(c => !c.reminder_email_sent)
                               .map(c => c.email));

    const sForLead     = recipients.filter(e => pendingSet.has(e));
    const sForReminder = recipients.filter(e => noRemSet.has(e));
    const invalid      = recipients.filter(e => !pendingSet.has(e) && !noRemSet.has(e));

    return { selectedForLead: sForLead, selectedForReminder: sForReminder, invalidSelection: invalid };
  }, [recipients, selected]);

  const handleSend = async (mode: 'lead' | 'reminder') => {
    if (!selected) return;
    const orgId = selected.org.id;
    const list = mode === 'lead' ? selectedForLead : selectedForReminder;
    if (list.length === 0) return;

    setSending(mode);
    try {
      await sendOrganizationEmail(
        orgId,
        list,
        mode === 'lead' ? 'lead_contact' : 'reminder'
      );
      setRecipients([]);
      reload();
    } catch (e) {
      console.error(e);
      alert(mode === 'lead' ? 'Error al enviar contacto' : 'Error al enviar reminder');
    } finally {
      setSending(null);
    }
  };

  const handleFinishAll = async () => {
    if (!selected) return;
    const orgId = selected.org.id;
    const confirmMsg = '¿Marcar TODOS los candidate_contacts de esta empresa como finalizados (first+reminder = TRUE)?';
    if (!window.confirm(confirmMsg)) return;

    setFinalizing(true);
    try {
      await finishAllContacts(orgId);
      setRecipients([]);
      reload();
    } catch (e) {
      console.error(e);
      alert('Error al finalizar todos los contactos');
    } finally {
      setFinalizing(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <OrgsTable
        items={tableItems as any}
        selectedId={selected?.org.id ?? null}
        onSelect={(row) => {
          const real = (items ?? []).find((x) => x.org.id === row.org.id) ?? null;
          setSelected(real);
          setRecipients([]);
        }}
      />

      <div className="grid grid-rows-[auto_auto_1fr_auto] gap-4">
        <CompanyInfo
          item={
            selected
              ? {
                  org: selected.org,
                  report: selected.report,
                  employees: [
                    ...(selected.employees_pending ?? []),
                    ...(selected.employees_contacted ?? []),
                  ],
                  total_candidates:
                    selected.total_candidates ??
                    (selected.employees_pending?.length ?? 0) +
                    (selected.employees_contacted?.length ?? 0),
                }
              : null
          }
        />

        <InProgressEmployeeSelector
          org={selected}
          selected={recipients}
          onChange={setRecipients}
        />

        {/* ── Botonera ── */}
        <div className="flex items-center gap-3">
          <button
            className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
            disabled={!selected || selectedForLead.length === 0 || sending !== null || finalizing}
            onClick={() => handleSend('lead')}
            title={selectedForLead.length === 0 ? 'Selecciona contactos pendientes' : ''}
          >
            {sending === 'lead' ? 'Enviando…' : `Enviar contacto (${selectedForLead.length})`}
          </button>

          <button
            className="px-3 py-2 rounded bg-amber-600 text-white disabled:opacity-50"
            disabled={!selected || selectedForReminder.length === 0 || sending !== null || finalizing}
            onClick={() => handleSend('reminder')}
            title={selectedForReminder.length === 0 ? 'Selecciona contactados sin reminder' : ''}
          >
            {sending === 'reminder' ? 'Enviando…' : `Enviar reminder (${selectedForReminder.length})`}
          </button>

          {/* Nuevo: Finalizar todos */}
          <button
            className="px-3 py-2 rounded bg-green-600 text-white disabled:opacity-50"
            disabled={!selected || sending !== null || finalizing}
            onClick={handleFinishAll}
            title="Marca todos los candidate_contacts como first+reminder = TRUE"
          >
            {finalizing ? 'Finalizando…' : 'Finalizar todos'}
          </button>

          {invalidSelection.length > 0 && (
            <span className="text-xs text-gray-500">
              {invalidSelection.length} fuera de los buckets (se ignorarán).
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default InProgressPane;
